import {
  auditTrailActionFilterKey,
  auditTrailEventSourceFilterKey,
  auditTrailIPAddressFilterKey,
  auditTrailMetadataFilterKey,
  auditTrailPrincipalFilterKey,
  auditTrailResourceFilterKey,
  auditTrailStatusFilterKey,
  auditTrailWorkspaceFilterKey,
} from '../components/AuditTrailPageControls/auditTrailFiltersConfig';
import { auditTrailComponentFilterKey } from '../components/AuditTrailPageControls/ComponentFilter';
import { auditTrailDateTimeFilterKey } from '../components/AuditTrailPageControls/DateTimeStartEndFilter';
import { auditTrailUserFilterKey } from '../components/AuditTrailPageControls/UserFilter';

/**
 * Configuration for how filters are handled in URL sync and API requests.
 * Each filter defines its type, URL parameters, and how it maps to the backend API.
 */
export interface FilterDefinition {
  key: string;
  type: 'string' | 'dateTime' | 'regex' | 'boolean' | 'metadata';
  urlParams: string[];
  requestMapping: {
    regex?: string;
    exact?: string;
    direct?: string;
  };
}

// Registry of all Audit Trail filters - single source of truth for filter configuration
export const filterRegistry: Record<string, FilterDefinition> = {
  [auditTrailDateTimeFilterKey]: {
    key: auditTrailDateTimeFilterKey,
    type: 'dateTime',
    urlParams: ['dateStart', 'dateEnd'],
    requestMapping: {
      direct: 'startEventTime',
    },
  },
  [auditTrailUserFilterKey]: {
    key: auditTrailUserFilterKey,
    type: 'string',
    urlParams: ['user'],
    requestMapping: {
      direct: 'principalExact',
    },
  },
  [auditTrailComponentFilterKey]: {
    key: auditTrailComponentFilterKey,
    type: 'string',
    urlParams: ['component'],
    requestMapping: {
      direct: 'eventSourceRegex',
    },
  },
  [auditTrailEventSourceFilterKey]: {
    key: auditTrailEventSourceFilterKey,
    type: 'regex',
    urlParams: ['eventSourceInput', 'eventSourceIsRegEx'],
    requestMapping: {
      regex: 'eventSourceRegex',
      exact: 'eventSourceExact',
    },
  },
  [auditTrailWorkspaceFilterKey]: {
    key: auditTrailWorkspaceFilterKey,
    type: 'regex',
    urlParams: ['workspaceInput', 'workspaceIsRegEx'],
    requestMapping: {
      regex: 'workspaceRegex',
      exact: 'workspaceExact',
    },
  },
  [auditTrailResourceFilterKey]: {
    key: auditTrailResourceFilterKey,
    type: 'regex',
    urlParams: ['resourceInput', 'resourceIsRegEx'],
    requestMapping: {
      regex: 'resourceRegex',
      exact: 'resourceExact',
    },
  },
  [auditTrailStatusFilterKey]: {
    key: auditTrailStatusFilterKey,
    type: 'string',
    urlParams: ['statusInput'],
    requestMapping: {
      direct: 'statusCodeRegex',
    },
  },
  [auditTrailPrincipalFilterKey]: {
    key: auditTrailPrincipalFilterKey,
    type: 'regex',
    urlParams: ['principalInput', 'principalIsRegEx'],
    requestMapping: {
      regex: 'principalRegex',
      exact: 'principalExact',
    },
  },
  [auditTrailIPAddressFilterKey]: {
    key: auditTrailIPAddressFilterKey,
    type: 'regex',
    urlParams: ['ipAddressInput', 'ipAddressIsRegEx'],
    requestMapping: {
      regex: 'sourceIpAddressRegex',
      exact: 'sourceIpAddressExact',
    },
  },
  [auditTrailActionFilterKey]: {
    key: auditTrailActionFilterKey,
    type: 'regex',
    urlParams: ['actionInput', 'actionIsRegEx'],
    requestMapping: {
      regex: 'actionRegex',
      exact: 'actionExact',
    },
  },
  [auditTrailMetadataFilterKey]: {
    key: auditTrailMetadataFilterKey,
    type: 'metadata',
    urlParams: ['metadata'],
    requestMapping: {
      direct: 'metadata',
    },
  },
};

// Helper to get all URL parameters for clearing
export const getAllFilterParams = (): string[] => {
  return Object.values(filterRegistry).flatMap((def) => def.urlParams);
};

// Helper to get filter definition by key
export const getFilterDefinition = (key: string): FilterDefinition | undefined => {
  return filterRegistry[key];
};
